import { useQuery } from '@apollo/client'
import gql from 'graphql-tag'

import { currentArtistVar } from '@/clients/utoniq-core'
import {
  ArtistBottomTabFragmentDoc,
  ArtistStoreCollectionBoxFragmentDoc,
  ArtistStoreForGuestQuery,
  ArtistStoreForGuestQueryVariables,
  ArtistStoreQuery,
  ArtistStoreQueryVariables,
  CurrentUserFragmentDoc,
  RecentViewedItemConnectionFragmentDoc,
  RecommendCollectionConnectionFragmentDoc,
  StoreCartQuery,
  StoreCartQueryVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useCurrentUser, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{ artistId: string }>()
  const { user, userClaims } = useCurrentUser()
  const artistId = router.query.artistId ?? config.artistId

  const { data, loading } = useQuery<
    ArtistStoreQuery,
    ArtistStoreQueryVariables
  >(QUERY, {
    variables: { artistId, storeArtistId: artistId },
    skip: !user?.isSigned,
    onCompleted(data) {
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })
  const { data: dataForGuest } = useQuery<
    ArtistStoreForGuestQuery,
    ArtistStoreForGuestQueryVariables
  >(QUERY_FOR_GUEST, {
    variables: { artistId, storeArtistId: artistId },
    skip: user?.isSigned,
    onCompleted(data) {
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })

  const { data: storeCartData } = useQuery<
    StoreCartQuery,
    StoreCartQueryVariables
  >(STORE_CART_QUERY, {
    variables: {
      pageInput: { limit: 100 },
    },
  })

  const storeCartCount =
    storeCartData?.userCarts?.edges.reduce((sum, edge) => {
      const itemCounts = edge.node.items?.map(item => item.count || 0) ?? []
      return sum + itemCounts.reduce((a, b) => a + b, 0)
    }, 0) ?? 0

  return {
    state: {
      artistId,
      isLoggedIn: !!user,
      userLoggedInId: user?.uid ?? '',
      point: data?.prepaidPointBalance?.formattedPrepaidPointBarance ?? '',
      user: data?.user ?? null,
      loading,
      collections: (data ?? dataForGuest)?.storeCollections ?? null,
      liveStreamId: (data ?? dataForGuest)?.artist?.currentLive?.id,
      followArtists: data?.user?.organizationFollowees ?? null,
      memberArtists: data?.user?.organizationPatrons ?? null,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      newMagazines:
        data?.recentlyStoreViewedItems.edges.map(e => e.node.item) || [],
      newCollections:
        data?.recentlyViewedCollections.edges.map(e => e.node.collection) || [],
      storeCartCount,
    },
    actions: {},
  }
})

const QUERY = gql`
  query ArtistStore($artistId: ID!, $storeArtistId: String!) {
    prepaidPointBalance {
      prepaidPointId
      balance
      formattedPrepaidPointBarance @client
    }
    user {
      ...CurrentUser
    }
    artist(id: $artistId) {
      ...ArtistBottomTab
      id
      currentLive {
        id
      }
    }
    storeCollections(artistId: $storeArtistId, pageInput: { limit: 6 }) {
      edges {
        node {
          ... on Collection {
            ...ArtistStoreCollectionBox
          }
        }
      }
    }
    recentlyViewedCollections(artistId: $storeArtistId) {
      ...RecommendCollectionConnection
    }
    recentlyStoreViewedItems(artistId: $storeArtistId) {
      ...RecentViewedItemConnection
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${CurrentUserFragmentDoc}
  ${ArtistStoreCollectionBoxFragmentDoc}
  ${RecommendCollectionConnectionFragmentDoc}
  ${RecentViewedItemConnectionFragmentDoc}
`

const QUERY_FOR_GUEST = gql`
  query ArtistStoreForGuest($artistId: ID!, $storeArtistId: String!) {
    artist(id: $artistId) {
      ...ArtistBottomTab
      id
      currentLive {
        id
      }
    }
    storeCollections(artistId: $storeArtistId, pageInput: { limit: 6 }) {
      edges {
        node {
          ... on Collection {
            ...ArtistStoreCollectionBox
          }
        }
      }
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${ArtistStoreCollectionBoxFragmentDoc}
`
export const STORE_CART_QUERY = gql`
  query StoreCart($pageInput: PageInput) {
    userCarts(pageInput: $pageInput) {
      edges {
        node {
          ... on UserCart {
            items {
              count
            }
          }
        }
      }
    }
  }
`
